'use client';

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../utils/cn";

/**
 * Skeleton — loading placeholder.
 *
 * Variants:
 *   text     — single-line body-14 (16px tall)
 *   heading  — heading-22 line (28px tall)
 *   circle   — round; use with `size` for avatar placeholders
 *   rect     — arbitrary rectangle; caller sets width/height via className
 *
 * The animation lives in `hauraki-skeleton` (theme.css) so it collapses to
 * static under `prefers-reduced-motion: reduce`.
 */

const skeletonVariants = cva(
  "hauraki-skeleton rounded-chip",
  {
    variants: {
      variant: {
        text: "h-4 w-full",
        heading: "h-7 w-2/3",
        circle: "rounded-full",
        rect: "w-full",
      },
      size: {
        24: "size-6",
        32: "size-8",
        40: "size-10",
        48: "size-12",
        64: "size-16",
      },
    },
    defaultVariants: { variant: "text" },
  },
);

export interface SkeletonProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof skeletonVariants> {}

export const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  function Skeleton({ className, variant, size, ...props }, ref) {
    return (
      <div
        ref={ref}
        data-slot="skeleton"
        data-variant={variant ?? "text"}
        aria-hidden="true"
        role="presentation"
        className={cn(skeletonVariants({ variant, size, className }))}
        {...props}
      />
    );
  },
);
Skeleton.displayName = "Skeleton";

export { skeletonVariants };
